import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Icon, MenuList, ToastContainer } from 'pepsico-ds';
import { appConstants } from '../../constants/appconstants';
import { getUserClaims, getAuthHeader } from '../../utils/auth/unified-auth';
import { KMSessionContext } from './KMSessionContext';
import MIChatActiveView from './MIChatActiveView';
import './MarketIntelligenceChat.scss';
import './SpendAnalyticsAgent.scss';
import './SpendAnalyticsConversations.scss';
import {
  normalizeSessionHistory,
  mapSessionSummaryItem,
  extractInsightsBlock,
  normalizeSessionMessages,
  getSpendAnalyticsCardVisibilityFromEnv,
} from './chatUtils';
import { SourceQueryItem, CitationItem } from './ChatSourceItems';
import { useDispatch, useSelector } from 'react-redux';
import { FetchUserInfoAction } from '../../redux/action/LandingPageAction';

let toastIdCounter = 0;

const KMConversations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const sessionContext = useContext(KMSessionContext);
  const dispatch = useDispatch();
  const userAccessData = useSelector((state) => state.getUserAccessReducer);
  const isAIUser = userAccessData?.isAiUser;
  const hasAgentMenuItems = isAIUser || getSpendAnalyticsCardVisibilityFromEnv();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [userInfo, setUserInfo] = useState({});

  const hasNavigationState =
    location.state !== null && location.state !== undefined;
  const initialThreadId =
    location.state?.conversation_id || location.state?.thread_id || null;
  const initialSessionId = location.state?.session_id || null;
  const initialMessage = location.state?.message || '';

  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);

  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const [expandedSourceItems, setExpandedSourceItems] = useState({});
  const [activeCitations, setActiveCitations] = useState([]);
  const [activeSqlQueries, setActiveSqlQueries] = useState([]);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [showAgentMenu, setShowAgentMenu] = useState(false);

  const [activeHistorySessionId, setActiveHistorySessionId] = useState(null);
  const [isSessionMessagesLoading, setIsSessionMessagesLoading] = useState(false);
  const [lastProcessedMessage, setLastProcessedMessage] = useState(null);
  const [plannerSteps, setPlannerSteps] = useState([]);
  const [sessionMessagesError, setSessionMessagesError] = useState('');
  const [sessionMessagesRetryable, setSessionMessagesRetryable] = useState(false);
  const [queryIntentDone, setQueryIntentDone] = useState(false);

  const [sessionHistory, setSessionHistory] = useState(() => sessionContext?.sessionHistory || []);
  const [historyError, setHistoryError] = useState('');
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState(null);
  const [recentMenuOpenId, setRecentMenuOpenId] = useState(null);
  const [pinLoadingById, setPinLoadingById] = useState({});

  const [feedbackToasts, setFeedbackToasts] = useState([]);
  const [submittedFeedbackByMessage, setSubmittedFeedbackByMessage] = useState({});
  const [feedbackSubmitPendingByMessage, setFeedbackSubmitPendingByMessage] = useState({});
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [messageFeedback, setMessageFeedback] = useState({});
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(null);

  const sessionMessagesRequestIdRef = useRef(0);
  const messagesContainerRef = useRef(null);
  const sseAbortRef = useRef(null);

  const displaySessionHistory = sessionHistory;

  const getFeedbackMessageKey = (messageIndex, messageData) => {
    // const scopedSessionId = sessionId || activeHistorySessionId || 'unknown';
    const scopedMessageId = messageData?.assistant_message_id 
      || messageData?.queryId 
      || messageData?.id 
      || `idx-${messageIndex}`;
    // return `${scopedSessionId}:${scopedMessageId}`;
    return `${scopedMessageId}`;
  };

  const pushFeedbackToast = (type, text, duration = 6000) => {
    setFeedbackToasts((prev) => [
      ...prev,
      { 
        id: ++toastIdCounter, 
        duration, 
        text, 
        type },
    ]);
  };

  const removeFeedbackToast = (toastId) => {
    setFeedbackToasts((prev) => prev
                                .filter((toast) => toast.id !== toastId));
  };

  const handleFeedbackTextChange = (text) => {
    setFeedbackText(text);
    if (text.length >= 500) { setFeedbackError('Comment must be 500 characters or fewer.'); } 
    else { setFeedbackError(''); }
  };

  const handleMessageFeedback = (messageIndex, feedbackType, messageData) => {

    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) { 
      return; 
    }

    if (submittedFeedbackByMessage[feedbackKey]) {

      const submittedThumb = submittedFeedbackByMessage[feedbackKey].thumbs;
      const clickedThumb = feedbackType === 'up' ? 'like' 
                                                 : 'unlike';
      if (submittedThumb === clickedThumb) 
      {
        handleResetFeedback(messageIndex, messageData, feedbackType);
        return;
      }
      return;
    }

    const currentFeedback = messageFeedback[messageIndex] || null;
    const nextFeedback = currentFeedback === feedbackType ? 
                                                          null : feedbackType;

    setMessageFeedback((prev) => ({ ...prev, [messageIndex]: nextFeedback }));
    setFeedbackError('');
    setFeedbackText('');

    if (feedbackType === 'up') {
      // Thumbs up: submit directly without opening the form
      if (nextFeedback) {
        handleSubmitFeedback(messageIndex, messageData, 'up');
      }
    } else {
      // Thumbs down: open form as usual
      setFeedbackFormOpen(nextFeedback ? messageIndex : null);
    }
  };

  const handleResetFeedback = async (messageIndex, messageData, feedbackType) => {
    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);
    if (feedbackSubmitPendingByMessage[feedbackKey]) {
      return;
    }

    const scopedSessionId = sessionId || activeHistorySessionId;
    if (!scopedSessionId) {
      return;
    }

    const queryId = messageData?.queryId || messageData?.id || null;
    if (!queryId || typeof queryId !== 'string') {
      return;
    }

    const selectedFeedback = feedbackType || messageFeedback[messageIndex] || null;

    const payload = {
      set_feedback: false,
      feedback_text: '',
      thumbs: selectedFeedback === 'up' ? 'like' : 'unlike',
      agent_id: 'KNOWLEDGE_MGMT',
      session_id: scopedSessionId,
      query_id: queryId.replace("-assistant", ""),
      message_id: feedbackKey || null,
    };

    setFeedbackSubmitPendingByMessage((prev) => ({ 
      ...prev, 
      [feedbackKey]: true 
    }));

    try {
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/feedback`,
        {
          headers: { Authorization, Accept: 'application/json', 'Content-Type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        setSubmittedFeedbackByMessage((prev) => {

          const next = { ...prev };
          delete next[feedbackKey];
          return next;

        });
        setMessageFeedback((prev) => {

          const next = { ...prev };
          delete next[messageIndex];
          return next;

        });
        pushFeedbackToast(
          'success', 
          'Feedback has been reset');
      } else {
        pushFeedbackToast(
          'error',
          'Unable to reset feedback. Please try again.');
      }
    } catch (err) {
      console.error('Feedback reset error:', err);
      pushFeedbackToast('error',
        'Unable to reset feedback. Please check your connection.');
    } finally {

      setFeedbackSubmitPendingByMessage((prev) => {

        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  };

  const handleCloseFeedbackForm = () => {
    setFeedbackText('');
    setFeedbackError('');
    setFeedbackFormOpen(null);
  };

  const handleSubmitFeedback = async (messageIndex, messageData, feedbackType) => {
    const selectedFeedback = feedbackType || messageFeedback[messageIndex] || null;

    if (!selectedFeedback) 
    {
      setFeedbackError('Select thumbs up or thumbs down to continue.');
      return;
    }

    const feedbackKey = getFeedbackMessageKey(messageIndex, messageData);

    if (feedbackSubmitPendingByMessage[feedbackKey]) { 
      return;
    }
    if (submittedFeedbackByMessage[feedbackKey]) {
      setFeedbackError('');
      pushFeedbackToast('info', 'Feedback already submitted for this response.');
      return;
    }

    const scopedSessionId = sessionId || activeHistorySessionId;

    if (!scopedSessionId) 
    {
      setFeedbackError('Unable to submit feedback right now. Missing session.');
      pushFeedbackToast('error', 
        'Unable to submit feedback right now. Please retry.');
      return;
    }

    const queryId = messageData?.queryId || messageData?.id || null;

    if (!queryId || typeof queryId !== 'string') 
    {
      setFeedbackError('Unable to submit feedback for this response.');
      pushFeedbackToast('error', 'Unable to submit feedback for this response. Please retry.');
      return;
    }

    const trimmedComment = feedbackText.trim();

    if (trimmedComment.length > 500) {
      setFeedbackError("You've reached the maximum character limit.");
      return;
    }

    const payload = {
      agent_id: 'KNOWLEDGE_MGMT',
      set_feedback: true,
      feedback_text: trimmedComment || '',
      message_id: messageData?.messageId || messageData?.id || null,
      thumbs: selectedFeedback === 'up' ? 'like' : 'unlike',
      query_id: queryId.replace("-assistant",""),
      session_id: scopedSessionId,
    };

    const params = new URLSearchParams();

    if (threadId) {
      params.set('conversation_id', threadId);
    }

    setFeedbackSubmitPendingByMessage((prev) => ({ ...prev, [feedbackKey]: true }));
    
    setFeedbackError('');

    try {
      const { Authorization } = await getAuthHeader();
      const url = `${appConstants.MI_AI_BASE_URL}/api/v1/feedback`;
      const res = await fetch(
        url,
        {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: { Authorization, Accept: 'application/json', 'Content-Type': 'application/json' },
        },
      );

      let responseData = null;
      try { 
        responseData = await res.json(); 
      } 
      catch (_) { responseData = null; }

      if (!res.ok) {
        if (res.status === 400) {
          const validationMessage = responseData?.message || 
          'Please check your feedback input and try again.';
          setFeedbackError(validationMessage);
          pushFeedbackToast('warning', 
            validationMessage);
          return;
        }
        const errorMessage = responseData?.message
         || 'Unable to save feedback right now. Please try again.';
        setFeedbackError(errorMessage);

        pushFeedbackToast('error', errorMessage);
        return;
      }

      setSubmittedFeedbackByMessage((prev) => ({
        ...prev,
        [feedbackKey]: {
          created_at: responseData?.created_at || new Date().toISOString(),
          feedback_text: responseData?.feedback_text || payload.feedback_text || '',
          thumbs: responseData?.thumbs || payload.thumbs,
          agent_id: payload.agent_id,
          message_id: responseData?.message_id || payload.message_id || null,
          query_id: responseData?.query_id || payload.query_id,
          session_id: responseData?.session_id || payload.session_id,
        },
      }));

      handleCloseFeedbackForm();
      pushFeedbackToast('success', 
        'Feedback submitted. Thank you!');
    } catch (err) {
      const networkMessage = 'Unable to save feedback right now. Please check your connection and retry.';
      console.error('Feedback submit error:', err);
      pushFeedbackToast('error', networkMessage);
      setFeedbackError(networkMessage);
    } finally {
      setFeedbackSubmitPendingByMessage((prev) => {
        const next = { ...prev };
        delete next[feedbackKey];
        return next;
      });
    }
  };

  const createSession = async (title) => {
    const { Authorization } = await getAuthHeader();
    const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
    const res = await fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=km`, {
      method: 'POST',
      headers: { Authorization, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title || '' }),
    });
    if (!res.ok) throw new Error(`Session creation failed: ${res.status}`);
    return res.json();
  };

  const openSSEStream = async (sid, text, tid) => {
    if (sseAbortRef.current) {
      sseAbortRef.current.abort();
    }
    const controller = new AbortController();

    sseAbortRef.current = controller;

    setShowRightSidebar(false);
    // State updates previously handled by fireMessage
    setMessages((prev) => [...prev, { type: 'user', text }]);
    setElapsedSeconds(0);
    setIsThinking(true);
    setPlannerSteps([]);
    setQueryIntentDone(false);

    const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
    const { Authorization } = await getAuthHeader();
    fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/query-km`, {
      method: 'POST',
      signal: controller.signal,
      headers: { Authorization, 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify({
        chat_id: tid || sid,
        session_id: sid,
        query: text,
      }),
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          setIsThinking(false);
          setMessages((prev) => [
            ...prev,
            { type: 'ai', 
              followUps: [] , 
              response: 'Something went wrong. Please try again.'
            },
          ]);
          return;
        }
        let buffer = '';
        const decoder = new TextDecoder();
        const reader = res.body.getReader();
        while (true) {
          const { done, value } = 
            await reader.read();
          if (done) {
            break;
          }
          buffer += decoder.decode(
            value, 
            { stream: true }
          );
          const parts = buffer.split('\n\n');

          buffer = parts.pop() ?? '';

          for (const part of parts) {
            if (!part.trim()) {
              continue;
            }
            let dataStr = '';
            let eventName = 'message';
            for (const line of part.split('\n')) {
              if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
              } 
              else if (line.startsWith('data:')) { dataStr += line.slice(5).trim(); }
            }
            if (!dataStr) {
              continue;
            }
            try {
              const eventData = JSON.parse(dataStr);
              switch (eventName) {
                case 'planner_info':
                  setPlannerSteps((prev) => [
                    ...prev,
                    { 
                      description: eventData.message || eventData.stage || '', 
                      status: 'complete' 
                    },
                  ]);
                  break;
                case 'planner_reasoning_step':
                  setPlannerSteps((prev) => [
                    ...prev,
                    { 
                      description: eventData.step || '', 
                      status: 'complete' 
                    },
                  ]);
                  break;
                case 'query_complete':
                case 'done':
                  setQueryIntentDone(true);
                  break;
                case 'final_response': {
                  const { insight, responseWithoutInsights } = extractInsightsBlock(eventData.response || '');
                  const citations = Array.isArray(eventData.citations)  ? 
                                    eventData.citations.filter((c) => c && typeof c === 'object').map((c) => ({ file_name: c.file_name || '', url: c.url || c.source || '' }))
                                  : [];
                  const sqlQueries = Array.isArray(eventData.sql_queries) ? 
                        eventData.sql_queries.filter((q) => typeof q === 'string' && q.trim()).map((q) => q.trim())
                        : [];
                  setMessages((prev) => [
                    ...prev,
                    {
                      type: 'ai',
                      id: eventData.assistant_message_id || eventData.query_id || eventData.queryId || `assistant-${Date.now()}`,
                      queryId: eventData.assistant_message_id || eventData.query_id || eventData.queryId || null,
                      messageId: eventData.assistant_message_id || eventData.message_id || eventData.messageId || null,
                      lastRefreshed: eventData.last_refreshed || null,
                      response: responseWithoutInsights,
                      insight,
                      followUps: eventData.follow_up_questions || [],
                      chartConfig: eventData.chart_config || null,
                      genieRawData: 
                        Array.isArray(eventData.genie_raw_data) 
                        ? eventData.genie_raw_data 
                        : null,
                      sqlQueries,
                      citations,
                      sourcesCount: citations.length + sqlQueries.length,
                    },
                  ]);
                  if (citations.length > 0 || sqlQueries.length > 0) {
                    setExpandedSourceItems({});
                    setActiveSqlQueries(sqlQueries);
                    setActiveCitations(citations);
                  } else {
                    setExpandedSourceItems({});
                    setActiveSqlQueries([]);
                    setActiveCitations([]);
                  }

                  setIsThinking(false);
                  break;
                }
                // case 'query_error':
                case 'error':
                  setMessages((prev) => [
                    ...prev,
                    { type: 'ai', 
                      response: eventData.error || eventData.message || 'Something went wrong. Please try again.', 
                      followUps: [],
                      isError: true
                    },
                  ]);
                  setIsThinking(false);
                  break;
                default:
                  break;
              }
            } catch (_e) {
              // ignore non-JSON event data
            }
          }
        }
        // Fallback: stop thinking if stream ends without final_response
        setIsThinking(false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setIsThinking(false);
          console.error('SSE error:', err);
          setMessages((prev) => [
            ...prev,
            {
              isError: true,
              followUps: [],
              type: 'ai',
              response: 'A network error occurred while processing your request. Please check your connection and try again.',
              
            },
          ]);
        }
      });
  };

  /* istanbul ignore next */
  const fireMessage = async (text, sid, tid) => {
    setMessages((prev) => [...prev, { type: 'user', text }]);
    setIsThinking(true);
    setQueryIntentDone(false);
    setElapsedSeconds(0);
    setPlannerSteps([]);

    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const body = { query: text };
      if (sid) body.session_id = sid;
      if (tid) body.chat_id = tid;
      await fetch(`${appConstants.MI_AI_BASE_URL}/api/v1/query-km`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { Authorization, 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.error('Background query error:', err);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || isThinking) {
      return; 
    } 
    setMessage('');
    const text = message.trim();

    let tid = threadId;
    let sid = sessionId;

    if (!sid) {
      try {
        const session = await createSession(text);
        tid = session.conversationId;
        sid = session.sessionId;
        
        setThreadId(tid);
        setSessionId(sid);

        // Persist session_id so a page refresh reloads the session instead of going blank
        navigate(
          location.pathname, 
          {
            replace: true,
            state: { session_id: sid, 
              conversation_id: tid },
        });

        const tempSession = {
          conversationId: '',
          sessionId: sid,
          lastMessage: text,
          title: text,
          isPinned: false,
          pinnedAt: null,
          messageCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastActiveAt: new Date().toISOString(),
        };
        setSessionHistory((prev) => [tempSession, ...prev]);
        fetchSessionHistory();
      } catch (err) {
        console.error('Session creation failed:', err);
        return;
      }
    }

    openSSEStream(sid, text, tid);
    // await fireMessage(text, sid, tid); // Non-Streaming API call
  };

  /* istanbul ignore next */
  useEffect(() => {
    async function fetchUserInfo() {
      const user = await getUserClaims();
      if (user) {
        setUserInfo(user);
        setIsUserDataFetched(true);
        localStorage.setItem('gpid', btoa(JSON.stringify(user?.gpid)));
        dispatch(FetchUserInfoAction(user));
      }
    }
    setIsDataLoading(true);
    fetchUserInfo();
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    return () => {
      dispatch({
        payload: 'RESET_USER_INFO_ACTION',
        type: 'RESET_USER_INFO_ACTION',
      });
    };
  }, []);

  // Thinking timer
  useEffect(() => {
    if (!isThinking) {
      return;
    }
    const interval = 
      setInterval(() => {
        setElapsedSeconds(
          (prev) => prev + 1
        );
      }, 1000);
    return () => clearInterval(interval);
  }, [isThinking]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (sseAbortRef.current) {
        sseAbortRef.current.abort();
      }
    };
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollTo({ behavior: 'smooth', top: container.scrollHeight });
  }, [messages, isThinking, plannerSteps]);

  // Sync active session id
  useEffect(() => {
    if (sessionId) {
      setActiveHistorySessionId(sessionId);
    }
  }, [sessionId]);

  // Fetch session history when sidebar opens
  useEffect(() => {
    if (showSidebar) {
      fetchSessionHistory().then((history) => {
        if (
          !activeHistorySessionId &&
          !hasNavigationState &&
          history &&
          history.length > 0
        ) {
          loadSessionMessages(history[0].sessionId, 
                              history[0].conversationId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar, location.key]);

  // Handle navigation state: load session or fire initial message
  useEffect(() => {
    // A REPLACE navigation is fired internally to persist session_id after creation — ignore it
    if (navigationType === 'REPLACE') {
      return;
    }
    if (sseAbortRef.current) {
      sseAbortRef.current.abort();
    }
    setPlannerSteps([]);
    setElapsedSeconds(0);
    setThreadId(null);
    setSessionId(null);
    setQueryIntentDone(false);
    setIsThinking(false);

    if (!hasNavigationState) {
      setMessages([]);
      setLastProcessedMessage(null);
      return;
    }

    if (navigationType === 'POP') {
      if (initialSessionId) {

        loadSessionMessages(initialSessionId, initialThreadId);
        return;
      }
      setMessages([]);
      setLastProcessedMessage(null);
      return;
    }

    if (!initialMessage && initialSessionId) {
      loadSessionMessages(initialSessionId, initialThreadId);
      setLastProcessedMessage(null);
      return;
    }

    if (!initialMessage) {
      setMessages([]);
      setLastProcessedMessage(null);
      return;
    }

    if (lastProcessedMessage === initialMessage) {
      return;
    }

    setMessage('');
    setMessages([]);

    let isCancelled = false;
    (async () => {
      try {
        let sid = initialSessionId;
        let tid = initialThreadId;

        if (!sid) {
          const session = await createSession(initialMessage);
          sid = session.sessionId;
          tid = session.conversationId;
        }

        if (isCancelled) return;
        setSessionId(sid);
        setThreadId(tid);
        setActiveHistorySessionId(sid);
        setLastProcessedMessage(initialMessage);

        // Persist session_id so a page refresh reloads the session instead of going blank
        navigate(
          location.pathname, 
          {
            replace: true,
            state: { 
                session_id: sid, 
                conversation_id: tid, 
                message: initialMessage },
          });

        setSessionHistory((prev) => {
          if (prev.some((s) => s.sessionId === sid)) return prev;
          return [
            {
              conversationId: '',
              sessionId: sid,
              title: initialMessage,
              messageCount: 0,
              lastMessage: initialMessage,
              isPinned: false,
              pinnedAt: null,
              updatedAt: new Date().toISOString(),
              lastActiveAt: new Date().toISOString(),
              createdAt: new Date().toISOString(),
            },
            ...prev,
          ];
        });

        openSSEStream(sid, initialMessage, tid);
        // await fireMessage(initialMessage, sid, tid); // Non-Streaming API call
      } catch (err) {
        if (!isCancelled) {
          console.error('Session bootstrap failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              response: 'Something went wrong. Please try again.',
              followUps: [],
              type: 'ai',
            },
          ]);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialThreadId,
    initialSessionId,
    initialMessage,
    hasNavigationState,
    navigationType,
    location.key,
  ]);

  const closeRecentMenu = () => setRecentMenuOpenId(null);

  const fetchSessionHistory = async () => {
    setHistoryError('');
    setIsHistoryLoading(true);
    try {
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=km`,
        // `https://apim-na-ai.dev.mypepsico.com/cgf/procurement-hub-ai-api/v1/api/v1/chats?chatbot=km`,
        {
          headers: { Authorization, 'Content-Type': 'application/json' },
          method: 'GET',
        },
      );
      if (!res.ok) { throw new Error(`Session history failed: ${res.status}`); }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);

      setSessionHistory(prev => {
        const serverIds = new Set(normalized
                                  .map(s => s.sessionId));
        const localOnly = prev
                          .filter(s => !serverIds.has(s.sessionId));

        const merged = [...localOnly, ...normalized];

        return merged.sort((a, b) => {
          if (a.isPinned !== b.isPinned) {
            return a.isPinned ? -1 : 1;
          }

          return new Date(b.lastActiveAt || 0) - new Date(a.lastActiveAt || 0);
        });
      });
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
      return normalized;
    } catch (err) {
      console.error('Session history error:', err);
      setHistoryError('Unable to load sessions.');
      setSessionHistory([]);
      return [];
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const loadSessionMessages = async (selectedSessionId, selectedConversationId) => {
    if (!selectedSessionId) return;

    if (sseAbortRef.current) sseAbortRef.current.abort();
    setIsThinking(false);
    setQueryIntentDone(false);
    setPlannerSteps([]);

    const requestId = ++sessionMessagesRequestIdRef.current;
    setThreadId(selectedConversationId || null);
    setActiveHistorySessionId(selectedSessionId);
    setShowRightSidebar(false);
    setSessionId(selectedSessionId);
    setSessionMessagesRetryable(false);
    setIsSessionMessagesLoading(true);
    setSessionMessagesError('');
    setMessages([]);

    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats/${selectedSessionId}/messages`,
        {
          headers: { Authorization, 'Content-Type': 'application/json' },
          method: 'GET',
        },
      );

      if (!res.ok) 
      {
        setSessionMessagesRetryable(res.status === 503 || res.status === 504);
        throw new Error(`Session messages failed: ${res.status}`);
      }

      const data = await res.json();
      if (requestId !== sessionMessagesRequestIdRef.current) return;
      const normalized = normalizeSessionMessages(data);
      setMessages(normalized);

      // Pre-populate feedback state from history messages
      const historyFeedback = {};
      const historySubmitted = {};
      normalized.forEach((msg, idx) => {
        if (msg.type === 'ai' && msg.isFeedbackActive && msg.feedbackThumbs) {
          const thumbDir = msg.feedbackThumbs === 'like' ? 'up' : 'down';
          historyFeedback[idx] = thumbDir;
          const key = getFeedbackMessageKey(idx, msg);
          historySubmitted[key] = { thumbs: msg.feedbackThumbs };
        }
      });
      setMessageFeedback(historyFeedback);
      setSubmittedFeedbackByMessage(historySubmitted);
      setFeedbackFormOpen(null);

      const lastAiMsg = [...normalized]
                        .reverse()
                        .find((m) => m.type === 'ai');
      const lastSqlQueries = Array.isArray(lastAiMsg?.sqlQueries) ? lastAiMsg.sqlQueries : [];
      const lastCitations = Array.isArray(lastAiMsg?.citations) ? lastAiMsg.citations : [];
      if (lastCitations.length > 0
         || lastSqlQueries.length > 0) {
        setActiveCitations(lastCitations);
        
        setExpandedSourceItems({});
        setActiveSqlQueries(lastSqlQueries);
      } else {
        
        setActiveSqlQueries([]);

        setActiveCitations([]);
        setExpandedSourceItems({});
      }
    } catch (err) {
      if (requestId !== sessionMessagesRequestIdRef.current) {
        return;
      }
      console.error('Session messages error:', err);
      setSessionMessagesError('Unable to load messages. Please retry.');
      setMessages([]);
    } finally {
      if (requestId === sessionMessagesRequestIdRef.current) {
        setIsSessionMessagesLoading(false);
      }
    }
  };

  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };


  const handleSendMessage = () => handleSend();


  return (
    <div className="mi-chat-container">
      <div className="mi-subheader-2">
        <div className="mi-subheader-left">
          <button
            className="mi-back-btn"
            onClick={() => navigate('/ai-assistant/knowledge-management')}
          >
            <Icon icon="arrow_back" style={{ color: '#006FCF' }} />
          </button>
          <button
            className="mi-sidebar-toggle-btn"
            onClick={() => setShowSidebar((prev) => !prev)}
            style={{ marginLeft: '6px' }}
          >
            <Icon icon="view_sidebar" style={{ color: '#006FCF' }} />
          </button>
          <div
            className="mi-agent-badge"
            style={{
              marginLeft: '6px',
              borderRadius: '9999px',
              borderColor: '#8947E6',
              background: 'linear-gradient(150deg, #591EAD 13.45%, #AD80E4 85.65%)',
            }}
          >KM</div>
          <span
            className="mi-agent-name"
            style={{ marginLeft: '8px', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => setShowAgentMenu((prev) => !prev)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAgentMenu((prev) => !prev); }}
          >
            Knowledge Management
          </span>
          {hasAgentMenuItems && (
            <Icon
              onClick={() => setShowAgentMenu((prev) => !prev)}
              style={{ cursor: 'pointer' }}
              icon="arrow_drop_down"
            />
          )}
        </div>

        <div className="mi-subheader-right">
          <button
            onClick={() => setShowRightSidebar((prev) => !prev)}
            className="mi-right-sidebar-toggle-btn mi-sidebar-toggle-btn"
          >
            <Icon icon="view_sidebar" style={{ color: '#006FCF' }} />
          </button>
        </div>
      </div>

      {showAgentMenu && hasAgentMenuItems && (
        <MenuList
          emptyStateMessage=""
          customNoResultMessage=""
          height={240}
          hideMenuListOnSelection
          className="ph-agent-select"
          isClearable
          items={[
            ...(isAIUser ? [{
              label: 'Market Intelligence',
              id: '/ai-assistant/market-intelligence',
            }] : []),
            ...(getSpendAnalyticsCardVisibilityFromEnv() ? [{
              label: 'Spend Analytics',
              id: '/spend-analytics-ai-agent',
            }] : []),
          ]}
          selectedItemCallback={function S8e(item) {
            if (item?.[0]?.id) {
              navigate(item[0].id);
            }
          }}
          showSearch={false}
          width=""
          type="single"
          style={{}}
          size="medium"
          selectedItems={[]}
          onHideMenuList={() => setShowAgentMenu(false)}
          listHeaderLabel=""
        />
      )}

      <div className="mi-chat-body">
        {showSidebar && (
          <div className="mi-chat-sidebar">
            <div className="mi-chats-header">
              <span>Chats</span>
              <button
                onClick={() => setShowSidebar(false)}
                className="mi-close-sidebar"
              >
                <Icon icon="close" />
              </button>
            </div>
            <button
              onClick={() => navigate('/ai-assistant/knowledge-management')}
              className="mi-new-chat-btn"
            >
              <span className="mi-new-chat-plus">+</span>
              <span>New chat</span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">Recents</div>
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">{historyError}</div>
              )}
              {isHistoryLoading && (
                <div className="mi-recent-item">Loading sessions...</div>
              )}
              {!isHistoryLoading && !historyError && displaySessionHistory.length === 0 && (
                  <div className="mi-recent-item">No sessions</div>
              )}
              {!isHistoryLoading && !historyError &&
                displaySessionHistory.map((chat) => (
                  <div key={chat.sessionId} className="sa-conv-recent-row">
                    <button
                      title={chat.title}
                      onClick={() => loadSessionMessages(chat.sessionId, chat.conversationId)}
                      className={`mi-recent-item sa-conv-recent-item${chat.sessionId === activeHistorySessionId ? ' mi-conv-recent-active' : ''}`}
                    >
                      <div className="sa-conv-recent-title-wrap" style={{ width: '100%', overflow: 'hidden' }}>
                        <span className="sa-conv-recent-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{chat.title}</span>
                      </div>
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mi-chat-main mi-chat-main--active">
          {isSessionMessagesLoading && (
            <div className="mi-recent-item" style={{ padding: '24px', textAlign: 'center' }}>Loading messages...</div>
          )}
          {!isSessionMessagesLoading && sessionMessagesError && (
            <div className="mi-recent-item" style={{ padding: '24px', color: '#c00' }}>
              <span>{sessionMessagesError}</span>
              {sessionMessagesRetryable && activeHistorySessionId && (
                <button
                  style={{ marginLeft: '8px' }}
                  type="button"
                  onClick={() => loadSessionMessages(activeHistorySessionId, threadId)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!isSessionMessagesLoading && !sessionMessagesError && (
            <MIChatActiveView
              chatbot="km"
              queryIntentDone={queryIntentDone}
              messages={messages}
              elapsedSeconds={elapsedSeconds}
              message={message}
              isThinking={isThinking}
              plannerSteps={plannerSteps}
              handleSendMessage={handleSendMessage}
              handleKeyPress={handleKeyPress}
              setMessage={setMessage}
              messagesContainerRef={messagesContainerRef}
              onShowSources={({ sqlQueries: qs, citations: cits }) => {
                setActiveSqlQueries(Array.isArray(qs) ? qs : []);
                setActiveCitations(Array.isArray(cits) ? cits : []);
                setExpandedSourceItems({});
                setShowRightSidebar(true);
              }}
              onFeedbackSubmit={handleSubmitFeedback}
              onFeedbackCancel={handleCloseFeedbackForm}
              onFeedbackTextChange={handleFeedbackTextChange}
              onMessageFeedback={handleMessageFeedback}
              getFeedbackMessageKey={getFeedbackMessageKey}
              submittedFeedbackByMessage={submittedFeedbackByMessage}
              feedbackSubmitPendingByMessage={feedbackSubmitPendingByMessage}
              feedbackText={feedbackText}
              feedbackFormOpen={feedbackFormOpen}
              messageFeedback={messageFeedback}
              feedbackError={feedbackError}
            />
          )}
        </div>

        {showRightSidebar && (
          <div className="mi-chat-sidebar mi-chat-sidebar--right">
            <div className="mi-chats-header">
              <span>Sources</span>
              <button
                onClick={() => setShowRightSidebar(false)}
                className="mi-close-sidebar"
              >
                <Icon icon="close" />
              </button>
            </div>
            <div className="mi-conv-sources-list">
              {activeCitations.length === 0 && activeSqlQueries.length === 0 && (
                <div className="sa-conv-sources-empty">No sources available.</div>
              )}
              {activeCitations.map((citation, index) => (
                <CitationItem
                  index={index}
                  citation={citation}
                  key={`citation-${index}-${citation.url}`}
                />
              ))}
              {activeSqlQueries.map((sourceText, index) => (
                <SourceQueryItem
                  isExpanded={Boolean(expandedSourceItems[index])}
                  index={activeCitations.length + index}
                  sourceText={sourceText}
                  key={`${index}-${sourceText.slice(0, 40)}`}
                  onToggle={() =>
                    setExpandedSourceItems((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }))
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <ToastContainer
        className="sa-conv-feedback-toast-container"
        style={{
          display: 'flex',
          width: '500px',
          maxWidth: '600px',
          padding: 'var(--Primitives-Spacing-spacing-8, 8px) var(--Primitives-Spacing-spacing-16, 16px)',
          alignItems: 'center',
          gap: '8px',
        }}
        data={feedbackToasts}
        position="bottom-center"
        showActionIcon={true}
        removeToast={removeFeedbackToast}
      />
    </div>
  );
};

export default KMConversations;
